/**
 * @fileoverview レポート・用語集の一覧等に表示する用のカードレイアウトコンポーネント（特集は除く）
 * @description 特集は StoryCard コンポーネントを使用する
 */
<script lang="ts">
import { defineComponent, inject } from 'vue'
import MessageReplyText from 'vue-material-design-icons/MessageReplyText.vue'
import { defaultProps, defaultFunction } from '@/components/_parials/contentsCard'
import PublisherName from '@/components/contents/PublisherName.vue'
import DateDefault from '@/components/date/DateDefault.vue'
import { DATE_FORMAT_JA_WITH_DAY_OF_WEEK } from '@/constants/dateFormat'
import { localeDateJa } from '@/utilities/formatter'

export default defineComponent({
  name: 'ContentsCard',

  components: {
    MessageReplyText,
    PublisherName,
    DateDefault,
  },

  props: {
    ...defaultProps,
  },

  setup(props) {
    // constants
    const THEMES = inject<Record<string, string>>('THEMES') || {}

    const {
      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,
    } = defaultFunction(props)

    return {
      // constants
      DATE_FORMAT_JA_WITH_DAY_OF_WEEK,
      THEMES,

      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,

      // methods
      localeDateJa,
    }
  },
})
</script>

<template>
  <component
    :is="wrapperTagName"
    v-if="item"
    :class="[
      'l-unit__contentsCard',
      sizeStateClass,
      { 'is-read': item.isRead },
    ]"
    :href="linkTo"
    :data-horizontal-pc="horizontal.pc"
    :data-horizontal-sp="horizontal.sp"
    :data-hero="hasHero && !!item.thumbImageUrl"
    :style="cssVariables">
    <div class="l-unit__contentsCard--header">
      <div
        :class="[
          'o-heading__contentsCard',
          sizeStateClass,
          { 'is-read': item.isRead },
        ]">
        <component
          :is="computedHeadingOptions.tagName"
          class="o-heading__contentsCard--item"
          :style="{
            'max-height': computedHeadingOptions.maxHeight,
            'line-height': computedHeadingOptions.lineHeight,
            'font-weight': computedHeadingOptions.fontWeight,
            '--font-size-pc': computedHeadingOptions.fontSize.pc,
            '--font-size-sp': computedHeadingOptions.fontSize.sp,
          }"
          v-html="highlightHeading" />
      <!-- /.o-heading__contentsCard --></div>
    <!-- /.l-unit__contentsCard header --></div>

    <div
      v-if="hasHero && item.thumbImageUrl"
      class="l-unit__contentsCard--image">
      <img
        class="img"
        :src="item.thumbImageUrl"
        loading="lazy"
        alt="">
    <!-- /.l-unit__contentsCard image --></div>

    <div
      v-if="item.description"
      class="l-unit__contentsCard--description">
      <div class="o-label__description">
        <p
          class="o-label__description--item"
          v-html="highlightDescription" />
      <!-- /.o-label__description --></div>
    <!-- /.l-unit__contentsCard description --></div>

    <div
      v-if="item.tags && item.tags.length"
      class="l-unit__contentsCard--action is-tag">
      <div class="o-list__tag">
        <ul class="o-list__tag--items">
          <li
            v-for="tag in item.tags"
            :key="tag.to"
            class="o-list__tag--item">
            <div class="o-action__tag">
              <a
                class="o-action__tag--item"
                :href="tag.to">
                # {{ tag.label }}
              </a>
            <!-- /.o-action__tag --></div>
          </li>
        </ul>
      <!-- /.o-list__tag --></div>
    <!-- /.l-unit__contentsCard action --></div>

    <div
      v-if="item.themeId"
      class="l-unit__contentsCard--footer is-theme">
      <div class="o-label__theme">
        <p class="o-label__theme--item">
          {{ THEMES[item.themeId] }}
        </p>
      <!-- /.o-label__theme --></div>
    <!-- /.l-unit__contentsCard footer --></div>

    <div
      v-if="item.comment?.total"
      class="l-unit__contentsCard--footer is-comment">
      <div class="o-label__totalComments">
        <p class="o-label__totalComments--item">
          <span class="o-label__totalComments--icon">
            <MessageReplyText />
          </span>
          <span class="o-label__totalComments--label">
            {{ item.comment.total }}
          </span>
        </p>
      <!-- /.o-label__totalComments --></div>
    <!-- /.l-unit__contentsCard footer --></div>

    <div
      v-if="item.publisher"
      class="l-unit__contentsCard--footer is-publisher">
      <PublisherName
        :image="item.publisher.iconUrl"
        :label="item.publisher.name" />
    <!-- /.l-unit__contentsCard footer --></div>

    <div
      v-if="item.vol"
      class="l-unit__contentsCard--footer is-vol">
      <div class="o-label__vol">
        <p class="o-label__vol--item">
          第{{ item.vol }}回
        </p>
      <!-- /.o-label__vol --></div>
    <!-- /.l-unit__contentsCard footer --></div>

    <div
      v-if="item.publishDate"
      class="l-unit__contentsCard--footer is-date">
      <DateDefault
        :date="item.publishDate"
        :format="DATE_FORMAT_JA_WITH_DAY_OF_WEEK"
        suffix="掲載" />
    <!-- /.l-unit__contentsCard footer --></div>
  <!-- /.l-unit__contentsCard --></component>
</template>

<style lang="sass" scoped>
$icon-width: 1.4em  // same size as solution logo in SolutionName.vue
$icon-margin: 0.4em

// layout
.l-unit__contentsCard
  $root: &
  text-decoration: none
  overflow: hidden
  margin-top: 1em
  +transition

  &:first-child
    margin-top: 0

  &[href]
    +mouseover
      .o-heading__contentsCard--item
        text-decoration: underline

      #{$root}--image
        opacity: 0.5

    &:has(#{$root}--footer.is-name.is-link:hover)
      .o-heading__contentsCard--item
        text-decoration: none

      #{$root}--image
        opacity: 1

    &:has(#{$root}--action.is-tag:hover)
      .o-heading__contentsCard--item
        text-decoration: none

      #{$root}--image
        opacity: 1

  & &--image
    +transition

    > .img
      height: 100%
      display: block
      object-fit: cover
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--description,
  & &--footer
    margin-top: 0.5em

  // Phablet 以上のとき
  +breakpoint($max: false, $min: true)
    // 縦型（PC）
    &[data-horizontal-pc="false"]
      $image-width: 200
      $image-height: 100
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      --image-aspect-pc: #{math.div($image-height, $image-width)}
      height: 100%
      display: grid
      grid-template-columns: auto auto auto 1fr
      align-items: center

      &[data-hero="false"]
        $image-margin: 0
        grid-template-rows: auto $image-margin auto 1fr auto auto auto auto

      &[data-hero="true"]
        $image-margin: 0.6em
        grid-template-rows: auto $image-margin auto 1fr auto auto auto auto

      #{$root}--header
        grid-column: 1 / 5
        grid-row: 4 / 5
        align-self: start

      #{$root}--image
        grid-column: 1 / 5
        grid-row: 1 / 2
        position: relative

        &:first-child:last-child
          margin-bottom: 0

        &::before
          content: ""
          width: 0
          display: block
          padding-top: rate(var(--image-aspect-pc))

      #{$root}--description
        grid-column: 1 / 5
        grid-row: 5 / 6

      #{$root}--action
        &.is-tag
          grid-column: 1 / 5
          grid-row: 6 / 7
          margin-top: 0.5em

      #{$root}--footer
        margin-top: 0.5em

        &.is-theme
          grid-column: 1 / 5
          grid-row: 7 / 8

        &.is-comment,
        &.is-publisher,
        &.is-date
          grid-row: 8 / 9

        &.is-comment
          grid-column: 1 / 2
          padding-right: 0.6em

        &.is-publisher
          min-width: 0  // for text-overflow in publisher label
          grid-column: 2 / 3

        &.is-date
          min-width: 0  // for text-overflow in date label
          grid-column: 3 / 4

        &.is-vol
          grid-column: 1 / 5
          grid-row: 3 / 4
          margin:
            top: 0
            bottom: 0.3em

    // 横型（PC）
    &[data-horizontal-pc="true"]
      $image-width: 200
      $image-height: 100
      $margin: 16
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      --image-aspect-pc: #{math.div($image-height, $image-width)}
      $image-area-size: calc(px(var(--image-width-pc)) + #{$margin}px)
      +base-layout($image-position: left, $root: $root, $image-width: $image-width, $image-height: $image-height, $margin-size: $margin)
      align-content: flex-start
      flex-direction: row
      flex-wrap: wrap
      flex: 0 0 auto

      #{$root}--header
        width: 100%
        flex-grow: 1

      #{$root}--description
        width: 100%

      #{$root}--action
        &.is-tag
          width: 100%
          order: -1
          line-height: 1
          margin-bottom: 0.5em

      #{$root}--footer
        &.is-theme
          width: 100%

        &.is-comment,
        &.is-publisher
          align-self: center

        &.is-comment
          padding-right: 0.6em

        &.is-publisher
          min-width: 0  // for text-overflow in publisher label

        &.is-date
          min-width: 0  // for text-overflow in date label
          align-self: center
          order: -1
          margin:
            top: 0
            bottom: 0.5em

        &.is-vol
          align-self: center
          order: -1
          margin:
            top: 0
            bottom: 0.5em
            right: 0.5em

      &[data-hero="true"]
        min-height: px(var(--image-height-pc))
        padding-left: $image-area-size

        #{$root}--image
          width: px(var(--image-width-pc))

          &::before
            content: ""
            width: 0
            display: block
            padding-top: rate(var(--image-aspect-pc))

      &[data-hero="false"]
        min-height: 0
        padding-left: 0

  +breakpoint
    #{$root}--description
      margin-top: 0.25em

    // 縦型（SP）
    &[data-horizontal-sp="false"]
      height: 100%
      display: grid
      grid-template-columns: auto auto auto 1fr
      align-items: center

      &[data-hero="false"]
        $image-margin: 0
        grid-template-rows: auto $image-margin auto 1fr auto auto auto auto

      &[data-hero="true"]
        $image-margin: 0.6em
        grid-template-rows: auto $image-margin auto 1fr auto auto auto auto

      #{$root}--header
        align-self: start
        grid-column: 1 / 5
        grid-row: 4 / 5

      #{$root}--image
        height: 100%
        grid-column: 1 / 5
        grid-row: 1 / 2
        align-self: start
        position: relative

        &:first-child:last-child
          margin-bottom: 0

        &::before
          content: ""
          width: 0
          display: block
          padding-top: 50%

      #{$root}--description
        grid-column: 1 / 5
        grid-row: 5 / 6

      #{$root}--action
        &.is-tag
          grid-column: 1 / 5
          grid-row: 6 / 7
          margin-top: 0.5em

      #{$root}--footer
        margin-top: 0.5em

        &.is-theme
          grid-column: 1 / 5
          grid-row: 7 / 8

        &.is-comment,
        &.is-publisher,
        &.is-date
          grid-row: 8 / 9

        &.is-comment
          grid-column: 1 / 2
          padding-right: 0.6em

        &.is-publisher
          min-width: 0  // for text-overflow in publisher label
          grid-column: 2 / 3

        &.is-date
          min-width: 0  // for text-overflow in date label
          grid-column: 3 / 4

        &.is-vol
          grid-column: 1 / 5
          grid-row: 3 / 4
          margin:
            top: 0
            bottom: 0.3em

    // 横型（SP）
    &[data-horizontal-sp="true"]
      $image-width: 140
      $image-height: 72
      $margin: 12
      --image-width-sp: #{$image-width}
      --image-height-sp: #{$image-height}
      --image-aspect-sp: #{math.div($image-height, $image-width)}
      $image-area-size: calc(px(var(--image-width-sp)) + #{$margin}px)
      +base-layout($image-position: left, $root: $root, $image-width: $image-width, $image-height: $image-height, $margin-size: $margin)
      align-content: flex-start
      flex-direction: row
      flex-wrap: wrap

      #{$root}--header
        width: 100%
        flex-grow: 1

      #{$root}--description
        width: 100%

      #{$root}--action
        &.is-tag
          width: 100%
          order: -1
          line-height: 1
          margin-bottom: 0.5em

      #{$root}--footer
        &.is-theme
          width: 100%

        &.is-comment,
        &.is-publisher
          align-self: center

        &.is-comment
          padding-right: 0.6em

        &.is-publisher
          min-width: 0  // for text-overflow in publisher label

        &.is-date
          min-width: 0  // for text-overflow in date label
          align-self: center
          order: -1
          margin:
            top: 0
            bottom: 0.5em

        &.is-vol
          align-self: center
          order: -1
          margin:
            top: 0
            bottom: 0.5em
            right: 0.5em

      &[data-hero="true"]
        min-height: px(var(--image-height-sp))
        padding-left: $image-area-size

        #{$root}--image
          width: px(var(--image-width-sp))

          &::before
            content: ""
            width: 0
            display: block
            padding-top: rate(var(--image-aspect-sp))

      &[data-hero="false"]
        min-height: 0
        padding-left: 0

// object
.o-heading__contentsCard
  $root: &
  --font-size-pc: $font-large
  --font-size-sp: $font-large

  & &--item
    overflow: hidden
    color: $font-black
    font-size: var(--font-size-pc)

  &.is-read
    #{$root}--item
      color: $font-x-light-gray

  +breakpoint
    #{$root}--item
      font-size: var(--font-size-sp)

.o-list__tag
  & &--items
    display: flex
    flex-wrap: wrap
    margin-left: -0.5em

  & &--item
    margin-left: 0.5em

.o-action__tag
  & &--item
    display: block
    line-height: 1.4
    color: $font-black
    font:
      size: $font-x-small
      weight: bold
    text-decoration: none
    +transition

    &:hover
      text-decoration: underline

.o-label__description
  $line-height: 1.6
  $max-line: 2
  & &--item
    max-height: #{$max-line * $line-height}em
    overflow: hidden
    line-height: $line-height
    color: $font-light-gray
    font-size: $font-small

.o-label__totalComments
  $root: &

  & &--item
    display: flex
    align-items: center
    line-height: 1
    color: $font-gray
    font-size: $font-x-small

  & &--icon
    width: $icon-width
    position: relative
    top: 0.05em
    margin-right: $icon-margin

.o-label__theme
  & &--item
    line-height: 1
    color: $font-gray
    font-size: $font-x-small

.o-label__vol
  $root: &

  padding: 0.2em
  background-color: rgba($bg-white, 1)
  border: 1px solid $border-dark-orange

  & &--item
    line-height: 1
    color: $font-dark-orange
    font-size: $font-x-small
    text-align: center
</style>
